import React, { useState, useEffect, useCallback } from 'react';
import Table from '../../../components/Table/Table';
import Button from '../../../components/Button/Button';
import InputField from '../../../components/InputField/InputField';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Pagination from '../../../components/Pagination/Pagination';
import { PaymentDetailInline } from '../../../components/AppointmentPaymentDetail/AppointmentPaymentDetail';
import { usePayments } from '../../../hooks/usePayments';
import type { TransactionFilters } from '../../../hooks/usePayments';
import { useToast } from '../../../contexts/ToastContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { isOnlinePaymentsEnabled } from '../../../lib/payments/onlinePaymentsFlag';
import { sumCapturedRevenue } from '../../../lib/payments/capturedRevenue';
import type { TransactionDto, CommissionDto, PayoutDto } from '../../../services/api';
import styles from './PaymentsPage.module.css';

type TabKey = 'transactions' | 'commissions' | 'payouts';

const PAYMENT_STATUS_OPTIONS = ['All Statuses', 'Completed', 'Pending', 'Failed', 'Refunded'];
// AB#2066 P1-A — localize the DISPLAY only; the English option value above stays the
// filter sentinel (statusFilter !== 'All Statuses' etc.), so filter/clear logic is untouched.
const PAYMENT_STATUS_LABEL_KEYS: Record<string, string> = {
    'All Statuses': 'payments.status.all',
    Completed: 'payments.status.completed',
    Pending: 'payments.status.pending',
    Failed: 'payments.status.failed',
    Refunded: 'payments.status.refunded',
};

const PaymentsPage: React.FC = () => {
    const { t } = useLanguage();

    // AB#2060 — Frozen V1 is pay-at-salon only. Online-payment concepts
    // (Commissions, Payouts, the Stripe PI column, and the Stripe payment
    // detail drawer) stay hidden behind this flag (default OFF). The Stripe
    // code is kept dormant, not deleted.
    const onlinePaymentsEnabled = isOnlinePaymentsEnabled();

    const [activeTab, setActiveTab] = useState<TabKey>('transactions');
    const [transactions, setTransactions] = useState<TransactionDto[]>([]);
    const [commissions, setCommissions] = useState<CommissionDto[]>([]);
    const [totalCommission, setTotalCommission] = useState(0);
    const [payouts, setPayouts] = useState<PayoutDto[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [pageSize] = useState(10);
    const [iban, setIban] = useState('');

    // Transaction filters
    const [statusFilter, setStatusFilter] = useState<string>('All Statuses');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    // Expandable row state (online-payments detail only)
    const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

    const { loading, error, getTransactions, getCommissions, getPayouts, updatePayoutIban } = usePayments();
    const { showToast } = useToast();

    const buildFilters = useCallback((): TransactionFilters => {
        const filters: TransactionFilters = {};
        if (statusFilter && statusFilter !== 'All Statuses') {
            filters.status = statusFilter;
        }
        if (startDate) filters.startDate = startDate;
        if (endDate) filters.endDate = endDate;
        return filters;
    }, [statusFilter, startDate, endDate]);

    const fetchData = useCallback(async () => {
        try {
            if (activeTab === 'transactions') {
                const filters = buildFilters();
                const res = await getTransactions(currentPage, pageSize, filters);
                setTransactions(res.transactions);
                setTotalCount(res.totalCount);
                // AB#2060 — Total Revenue reconciles with the CAPTURED (collected) pay-at-salon
                // transactions only; Failed/Pending rows are shown but not counted as revenue.
                setTotalRevenue(res.totalRevenue ?? sumCapturedRevenue(res.transactions));
            } else if (activeTab === 'commissions') {
                const res = await getCommissions(currentPage, pageSize);
                setCommissions(res.commissions);
                setTotalCommission(res.totalCommission);
                setTotalCount(res.totalCount);
            } else {
                const res = await getPayouts(currentPage, pageSize);
                setPayouts(res.payouts);
                setTotalCount(res.totalCount);
            }
        } catch {
            // error state handled by hook
        }
    }, [activeTab, currentPage, pageSize, getTransactions, getCommissions, getPayouts, buildFilters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTabChange = (tab: TabKey) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setExpandedRowIndex(null);
    };

    const handleUpdateIban = async () => {
        if (!iban.trim()) return;
        try {
            await updatePayoutIban(iban.trim());
            showToast('IBAN updated successfully.', 'success');
        } catch {
            showToast('Failed to update IBAN.', 'error');
        }
    };

    const handleStatusFilterChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            setStatusFilter(value);
            setCurrentPage(1);
            setExpandedRowIndex(null);
        }
    };

    const handleClearFilters = () => {
        setStatusFilter('All Statuses');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
        setExpandedRowIndex(null);
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const statusClass = (status: string) => {
        const s = status.toLowerCase();
        if (s === 'completed' || s === 'paid' || s === 'captured') return styles.statusCompleted;
        if (s === 'pending') return styles.statusPending;
        if (s === 'failed') return styles.statusFailed;
        if (s === 'refunded') return styles.statusRefunded;
        return '';
    };

    const truncatePI = (pi?: string): string => {
        if (!pi) return '-';
        if (pi.length <= 16) return pi;
        return `${pi.substring(0, 8)}...${pi.substring(pi.length - 4)}`;
    };

    const handleToggleExpand = (rowIndex: number) => {
        setExpandedRowIndex(prev => (prev === rowIndex ? null : rowIndex));
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    const hasActiveFilters = statusFilter !== 'All Statuses' || startDate !== '' || endDate !== '';

    // Localized column labels (also used as the Table's row-data keys).
    const COL = {
        date: t('payments.col.date'),
        appointment: t('payments.col.appointment'),
        stripePi: t('payments.col.stripePi'),
        amount: t('payments.col.amount'),
        status: t('payments.col.status'),
        method: t('payments.col.method'),
    };

    // Pay-at-salon keeps date/appointment/amount/status/method. The Stripe PI
    // column and the expandable Stripe detail row only appear when online
    // payments are enabled (AB#2060).
    const transactionHeaders = onlinePaymentsEnabled
        ? [COL.date, COL.appointment, COL.stripePi, COL.amount, COL.status, COL.method, '']
        : [COL.date, COL.appointment, COL.amount, COL.status, COL.method];

    const transactionData = transactions.map((tx, i) => {
        const row: { [key: string]: React.ReactNode; originalIndex: number } = {
            originalIndex: i,
            [COL.date]: formatDate(tx.date),
            [COL.appointment]: `#${tx.appointmentId}`,
            [COL.amount]: `${tx.amount.toFixed(2)} ${(tx.currency || 'AED').toUpperCase()}`,
            [COL.status]: <span className={`${styles.statusBadge} ${statusClass(tx.status)}`}>{tx.status}</span>,
            [COL.method]: tx.method,
        };
        if (onlinePaymentsEnabled) {
            row[COL.stripePi] = truncatePI(tx.stripePaymentIntentId);
            row[''] = (
                <button
                    className={styles.expandButton}
                    onClick={(e) => { e.stopPropagation(); handleToggleExpand(i); }}
                    title="View details"
                >
                    {expandedRowIndex === i ? '▲' : '▼'}
                </button>
            );
        }
        return row;
    });

    const commissionHeaders = ['Appointment', 'Rate', 'Amount', 'Status'];
    const commissionData = commissions.map((c, i) => ({
        originalIndex: i,
        'Appointment': `#${c.appointmentId}`,
        'Rate': `${c.rate}%`,
        'Amount': `$${c.amount.toFixed(2)}`,
        'Status': <span className={`${styles.statusBadge} ${statusClass(c.status)}`}>{c.status}</span>
    }));

    const payoutHeaders = ['Period', 'Amount', 'Status'];
    const payoutData = payouts.map((p, i) => ({
        originalIndex: i,
        'Period': p.period,
        'Amount': `$${p.amount.toFixed(2)}`,
        'Status': <span className={`${styles.statusBadge} ${statusClass(p.status)}`}>{p.status}</span>
    }));

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>{t('payments.title')}</h1>
                <p className={styles.subtitle}>
                    {onlinePaymentsEnabled ? t('payments.subtitle.online') : t('payments.subtitle.payAtSalon')}
                </p>
            </div>

            {onlinePaymentsEnabled && (
                <div className={styles.tabs}>
                    <button className={`${styles.tab} ${activeTab === 'transactions' ? styles.tabActive : ''}`} onClick={() => handleTabChange('transactions')}>{t('payments.tab.transactions')}</button>
                    <button className={`${styles.tab} ${activeTab === 'commissions' ? styles.tabActive : ''}`} onClick={() => handleTabChange('commissions')}>{t('payments.tab.commissions')}</button>
                    <button className={`${styles.tab} ${activeTab === 'payouts' ? styles.tabActive : ''}`} onClick={() => handleTabChange('payouts')}>{t('payments.tab.payouts')}</button>
                </div>
            )}

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                    <Button label={t('payments.retry')} onClick={fetchData} size="small" />
                </div>
            )}

            {/* Revenue Summary for Transactions */}
            {activeTab === 'transactions' && transactions.length > 0 && (
                <div className={styles.summaryCard}>
                    <div className={styles.summaryItem}>
                        <p className={styles.summaryLabel}>{t('payments.summary.totalRevenue')}</p>
                        <p className={styles.summaryValue}>{totalRevenue.toFixed(2)} AED</p>
                    </div>
                    <div className={styles.summaryItem}>
                        <p className={styles.summaryLabel}>{t('payments.summary.transactions')}</p>
                        <p className={styles.summaryValue}>{totalCount}</p>
                    </div>
                </div>
            )}

            {/* Transaction Filters */}
            {activeTab === 'transactions' && (
                <div className={styles.filterSection}>
                    <div className={styles.filterRow}>
                        <div className={styles.filterItem}>
                            <Dropdown
                                label={t('payments.filter.status')}
                                options={PAYMENT_STATUS_OPTIONS}
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                getOptionLabel={(o) => {
                                    const k = PAYMENT_STATUS_LABEL_KEYS[o];
                                    return k ? t(k) : o;
                                }}
                                variant="secondary"
                            />
                        </div>
                        <div className={styles.filterItem}>
                            <label className={styles.filterLabel}>{t('payments.filter.from')}</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                                className={styles.dateInput}
                            />
                        </div>
                        <div className={styles.filterItem}>
                            <label className={styles.filterLabel}>{t('payments.filter.to')}</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                                className={styles.dateInput}
                            />
                        </div>
                        {hasActiveFilters && (
                            <Button label={t('payments.filter.clear')} onClick={handleClearFilters} size="small" variant="ghost" />
                        )}
                    </div>
                </div>
            )}

            {onlinePaymentsEnabled && activeTab === 'commissions' && commissions.length > 0 && (
                <div className={styles.summaryCard}>
                    <div className={styles.summaryItem}>
                        <p className={styles.summaryLabel}>Total Commission</p>
                        <p className={styles.summaryValue}>${totalCommission.toFixed(2)}</p>
                    </div>
                </div>
            )}

            {onlinePaymentsEnabled && activeTab === 'payouts' && (
                <div className={styles.ibanSection}>
                    <div className={styles.ibanInput}>
                        <InputField label="Payout IBAN" value={iban} onChange={setIban} placeholder="Enter your IBAN" />
                    </div>
                    <Button label="Update IBAN" onClick={handleUpdateIban} disabled={loading || !iban.trim()} />
                </div>
            )}

            <div className={styles.tableWrapper}>
                {activeTab === 'transactions' && (
                    <>
                        <Table
                            headers={transactionHeaders}
                            data={transactionData}
                            loading={loading}
                            nonSortableColumns={[COL.status, '']}
                            customRenderers={{
                                [COL.status]: (v) => v,
                                '': (v) => v,
                            }}
                        />
                        {/* Expandable Stripe detail row (online payments only) */}
                        {onlinePaymentsEnabled && expandedRowIndex !== null && transactions[expandedRowIndex] && (
                            <div className={styles.expandedRow}>
                                <PaymentDetailInline transaction={transactions[expandedRowIndex]} />
                            </div>
                        )}
                    </>
                )}
                {onlinePaymentsEnabled && activeTab === 'commissions' && (
                    <Table headers={commissionHeaders} data={commissionData} loading={loading} nonSortableColumns={['Status']}
                        customRenderers={{ 'Status': (v) => v }} />
                )}
                {onlinePaymentsEnabled && activeTab === 'payouts' && (
                    <Table headers={payoutHeaders} data={payoutData} loading={loading} nonSortableColumns={['Status']}
                        customRenderers={{ 'Status': (v) => v }} />
                )}
            </div>

            {!loading && totalPages > 1 && (
                <div className={styles.paginationWrapper}>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
            )}

            {!loading && !error && totalCount === 0 && (
                <div className={styles.emptyState}>
                    <h3>{t('payments.empty.title')}</h3>
                    <p>{t('payments.empty.body')}</p>
                </div>
            )}
        </div>
    );
};

export default PaymentsPage;
